import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { ConfirmDialog, Textarea } from '@jumpstone/ui-react';
import type {
  ExpenseActionFailure,
  ExpenseActionResult,
} from '../../data/use_expenses';
import type { Expense } from '../../data/types';

const MAX_REJECTION_COMMENT_LENGTH = 1000;

/**
 * Reject confirmation dialog with a required reason field. The real
 * `expenseClaim.reject` mutation requires a non-empty `comment`
 * (`z.string().min(1).max(1000)`, `apps/api/src/trpc/routers/
 * expense_claims.ts`) — the old mock-driven Reject button had no reason
 * input at all, so this dialog is new UI needed purely to satisfy the real
 * backend's contract (#22792).
 */
export function ExpenseRejectDialog({
  expense,
  onOpenChange,
  reject,
  onFailure,
}: {
  /** The claim being rejected, or `null` when the dialog is closed. */
  expense: Expense | null;
  onOpenChange: (open: boolean) => void;
  reject:
    | ((claim_id: string, comment: string) => Promise<ExpenseActionResult>)
    | undefined;
  onFailure: (failure: ExpenseActionFailure) => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [comment, set_comment] = useState('');
  const [is_submitting, set_is_submitting] = useState(false);
  const [touched, set_touched] = useState(false);

  const trimmed = comment.trim();
  const is_valid =
    trimmed.length > 0 && trimmed.length <= MAX_REJECTION_COMMENT_LENGTH;

  const handle_confirm = (): void => {
    if (!expense || !reject) return;
    if (!is_valid) {
      set_touched(true);
      return;
    }
    set_is_submitting(true);
    void reject(expense.id, trimmed)
      .then((result) => {
        if (result.outcome === 'success') {
          set_comment('');
          onOpenChange(false);
        } else {
          onFailure(result.failure);
        }
      })
      .finally(() => {
        set_is_submitting(false);
      });
  };

  return (
    <ConfirmDialog
      open={expense !== null}
      onOpenChange={(open) => {
        if (!open) {
          set_comment('');
          set_touched(false);
        }
        onOpenChange(open);
      }}
      title={t('expenses.reject_dialog_title', {
        claim_number: expense?.claim_number ?? '',
      })}
      description={t('expenses.reject_dialog_description')}
      confirmLabel={t('common.reject')}
      cancelLabel={t('common.cancel')}
      variant="destructive"
      loading={is_submitting}
      onConfirm={handle_confirm}
    >
      <Textarea
        value={comment}
        onChange={(event) => {
          set_comment(event.target.value);
        }}
        placeholder={t('expenses.reject_reason_placeholder')}
        aria-label={t('expenses.reject_reason_label')}
        maxLength={MAX_REJECTION_COMMENT_LENGTH}
        disabled={is_submitting}
        error={touched && !is_valid}
      />
      {touched && !is_valid && (
        <p className="nv-help is-error mt-1" role="alert">
          {t('expenses.reject_reason_required')}
        </p>
      )}
    </ConfirmDialog>
  );
}
